/**
 * The blocks every layout shares: the mode control (who owns PHI right now,
 * and the one button that changes it), the two systems side by side with
 * their realtime usage, the precondition strip and the global action bar.
 *
 * They are shared so layouts differ in INFORMATION ARCHITECTURE (how the
 * same facts are arranged), not in behaviour -- a flag flipped in one
 * layout behaves exactly as it does in any other.
 *
 * Colour language (see rdsUi.js): GREEN = this system is serving right now,
 * RED = it is not, both green = both in use. The word says which system.
 */
import React from "react";
import {
  Box, HStack, VStack, Text, Button, Switch, Badge, Menu, MenuButton, MenuList, MenuItem,
} from "@chakra-ui/react";
import {
  InfoOutlineIcon, RepeatIcon, ExternalLinkIcon, ViewIcon, ChevronDownIcon,
} from "@chakra-ui/icons";
import { formatAge } from "../../../utils/resultCache";
import {
  Dot, StatePill, PanelCard, SectionLabel, SystemPair, ActivityBars, UsageRow,
  SOURCE, ON_TONE, OFF_TONE,
} from "./rdsUi";

/**
 * SOURCE OF TRUTH — the mode switch.
 *
 * RDS keeps ONE row saying where PHI rows come from:
 *   "mirror"       -> Supabase is the truth, the whole-table mirror copies
 *                     PHI into RDS, and a per-table switch only changes
 *                     which copy is read.
 *   "independent"  -> RDS is the truth for PHI, the mirror stops, the app
 *                     forwards its writes, and a narrow catch-up reconciles
 *                     the backend writers that still hit Supabase only.
 *   "supabase_only"-> RDS is paused: the app uses Supabase alone and RDS
 *                     pulls nothing, keeping its data for a later return.
 *
 * Two different switches live on this card, and the card keeps them apart:
 *
 *   "Use Supabase only" / "Enable RDS"  -- THIS NotesAI's own switch
 *       (backend/api/rds_local_switch.py, vm.localPaused). Only this
 *       deployment stops / resumes using RDS; RDS is never told, keeps its
 *       mode and its jobs, and serves every other server as before.
 *   "RDS's own mode" (menu)             -- RDS's one source-mode row, which
 *       every server follows: make independent / return to mirror / turn a
 *       self-paused RDS back on. Global by nature, so it is a step away.
 */
export function SourceOfTruthCard({ vm, actions, busy }) {
  const independent = vm.independent;
  const paused = !!vm.paused;
  const localPaused = !!vm.localPaused;
  const server = vm.localSwitch?.server;
  const pinned = vm.pinned;
  const catchupOn = vm.catchupOn;
  // The mode ROW may still say "independent" while the kill switch is on
  // -- then RDS owns nothing and every read is back on Supabase. The card
  // must say that, not "RDS owns the PHI rows". A paused RDS reports the
  // kill switch too, but that is the chosen mode, not a veto.
  const vetoed = (!!vm.killed && !paused && !localPaused) || !vm.buildOn || !vm.liveness?.reachable;
  // Green while the stated owner really is serving; red when RDS is vetoed
  // or deliberately switched off (here or on RDS itself).
  const tone = vetoed || paused || localPaused ? OFF_TONE : ON_TONE;
  const rdsModeWord = paused ? "PAUSED (its own switch)" : independent ? "INDEPENDENT" : "MIRROR";

  return (
    <PanelCard
      tone={tone}
      testid="rds-source-of-truth"
      title="Source of truth for PHI"
      subtitle={
        localPaused
          ? `RDS is switched off for this NotesAI${server ? ` (${server})` : ""}: the app reads and writes Supabase alone. RDS itself was not changed — its own mode is still ${rdsModeWord} and every other server keeps using it.`
          : vetoed
            ? "Right now every read is served from Supabase: the kill switch, a missing RDS_API_URL or RDS itself is stopping RDS. The mode below is what RDS's own row still says."
            : independent
              ? "RDS owns the PHI rows: the whole-table mirror is stopped and the app writes PHI to RDS directly. Supabase serves non-PHI columns and sign-in."
              : paused
                ? "Supabase only: the app reads and writes Supabase alone and RDS is paused by its own switch. Nothing is mirrored into RDS, so it keeps the data it had until you come back."
                : "Supabase owns every row; the whole-table mirror copies PHI into RDS and any switch below only changes which copy is read."
      }
      right={
        <HStack spacing={2} flexWrap="wrap" justify="flex-end">
          {/* THIS NotesAI's switch: never touches RDS. */}
          {localPaused ? (
            <Button
              size="sm"
              colorScheme="green"
              onClick={actions.enableRds}
              isDisabled={!!busy}
              title="Use RDS again in this NotesAI (RDS itself is not changed)"
              data-testid="rds-enable-rds"
            >
              Enable RDS
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              onClick={actions.useSupabaseOnly}
              isDisabled={!!busy}
              title="Stop using RDS in this NotesAI only — RDS keeps running for every other server"
              data-testid="rds-use-supabase-only"
            >
              Use Supabase only
            </Button>
          )}
          {!paused && (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<RepeatIcon />}
              onClick={actions.syncMirror}
              isLoading={busy === "sync"}
              isDisabled={!!busy && busy !== "sync"}
              data-testid="rds-sync-mirror"
            >
              {independent ? "Reconcile + sync roles" : "Sync mirror"}
            </Button>
          )}
          {independent && (
            <HStack spacing={2}>
              <Switch
                size="sm"
                colorScheme="green"
                isChecked={catchupOn}
                isDisabled={pinned || !!busy}
                onChange={(e) => actions.setCatchup(e.target.checked)}
                data-testid="rds-catchup-toggle"
              />
              <Text fontSize="xs" color="gray.600">Catch-up reconciliation</Text>
            </HStack>
          )}
          {/* RDS's OWN mode: global, every server follows it. Kept one step
              away so it is never pressed by habit instead of the local switch. */}
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              rightIcon={<ChevronDownIcon />}
              isDisabled={!!busy}
              title={pinned ? vm.actions.pinnedNote : "Change RDS's own mode — this affects every server that uses RDS"}
              data-testid="rds-own-mode-menu"
            >
              RDS's own mode
            </MenuButton>
            <MenuList fontSize="sm">
              <Box px={3} py={1}>
                <Text fontSize="xs" color="gray.500">
                  RDS is <b>{rdsModeWord}</b>. Changing it affects every server, not just this NotesAI.
                </Text>
              </Box>
              {paused ? (
                <MenuItem onClick={actions.goIndependent} isDisabled={pinned} data-testid="rds-enable-rds-itself">
                  Turn RDS itself back on (independent)…
                </MenuItem>
              ) : (
                <MenuItem onClick={actions.goIndependent} isDisabled={pinned || independent}
                          data-testid="rds-go-independent">
                  Make RDS independent (source of truth)…
                </MenuItem>
              )}
              <MenuItem onClick={actions.goMirror} isDisabled={pinned || (!independent && !paused)}
                        data-testid="rds-go-mirror">
                Return RDS to mirror mode (re-clone from Supabase)…
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      }
    >
      <HStack spacing={3} flexWrap="wrap" align="center">
        <HStack spacing={2}>
          <Dot tone={tone} size="10px" boxShadow />
          <Badge colorScheme={tone} fontSize="xs" px={2} data-testid="rds-source-mode-badge">
            {localPaused
              ? `SUPABASE ONLY · RDS OFF IN THIS NOTESAI since ${vm.localSwitch?.since ? formatAge(vm.localSwitch.since) : "?"} · RDS ITSELF: ${rdsModeWord}`
              : vetoed
              ? "SUPABASE IS SERVING EVERYTHING"
              : independent
                ? `RDS · INDEPENDENT since ${vm.sourceMode?.since ? formatAge(vm.sourceMode.since) : "?"}${catchupOn ? "" : " · catch-up off"}`
                : paused
                  ? `SUPABASE ONLY · RDS PAUSED since ${vm.sourceMode?.since ? formatAge(vm.sourceMode.since) : "?"}`
                  : "SUPABASE · MIRROR MODE"}
          </Badge>
        </HStack>
        <SystemPair systems={vm.systems} size="xs" testidPrefix="rds-sot-pair" />
        {pinned && (
          <StatePill tone="gray">{vm.actions.pinnedNote}</StatePill>
        )}
        {localPaused && (
          <Text fontSize="xs" color="gray.600" data-testid="rds-local-paused-note">
            Only this NotesAI stopped using RDS
            {vm.localSwitch?.note ? ` (“${vm.localSwitch.note}”)` : ""}
            . RDS's own sync jobs and every other server are unaffected. Press Enable RDS to use it here again
            {independent ? " — rows this NotesAI wrote to Supabase meanwhile reach RDS through catch-up or Sync RDS ⇄ Supabase." : "."}
          </Text>
        )}
        {vetoed && !localPaused && (
          <Text fontSize="xs" color="red.600">
            {vm.killed
              ? "The RDS kill switch is on: every switch below is vetoed and every read falls back to Supabase."
              : !vm.buildOn
                ? "This NotesAI never asks RDS (RDS_API_URL is not set on the backend), so every read falls back to Supabase."
                : "RDS is not answering, so every read falls back to Supabase."}
          </Text>
        )}
        {paused && !vetoed && !localPaused && (
          <Text fontSize="xs" color="gray.500">
            RDS is paused by its own switch: no mirror, no catch-up, no file sync, for every server. Turn it back
            on under RDS's own mode (catch-up then copies the rows that changed in Supabase meanwhile; review
            files in Sync RDS ⇄ Supabase).
          </Text>
        )}
        {!independent && !paused && !vetoed && !localPaused && (
          <Text fontSize="xs" color="gray.500">
            The mirror is running: RDS holds a copy of every PHI table
            {vm.sources.rds.active ? ", and the switches below say which reads are served from it." : ", and no read is served from it yet."}
          </Text>
        )}
      </HStack>
    </PanelCard>
  );
}

/**
 * The global action bar. Same actions in every layout, so the muscle
 * memory survives a layout change.
 */
export function ActionBar({ vm, actions, busy, extra }) {
  return (
    <HStack spacing={2} flexWrap="wrap" justify="flex-end">
      {extra}
      {actions.twoWaySync && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<RepeatIcon />}
          onClick={actions.twoWaySync}
          isDisabled={!!busy}
          data-testid="rds-two-way-sync"
          title="Compare RDS and Supabase and copy each change the way it belongs, after you review it"
        >
          Sync RDS ⇄ Supabase
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        leftIcon={<ViewIcon />}
        onClick={actions.openLogs}
        data-testid="rds-logs"
      >
        Logs
        <Badge ml={2} colorScheme="purple" fontSize="9px">{vm.activity.total}</Badge>
      </Button>
      {vm.storage.present && (
        <Button
          size="sm"
          variant="outline"
          onClick={actions.syncStorage}
          isLoading={busy === "sync-storage"}
          isDisabled={!!busy && busy !== "sync-storage"}
          data-testid="rds-sync-storage"
        >
          Sync buckets
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        leftIcon={<ExternalLinkIcon />}
        onClick={actions.openAdmin}
        isLoading={busy === "open"}
        isDisabled={!!busy && busy !== "open"}
        data-testid="rds-open-admin"
      >
        Open RDS admin
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={actions.refresh}
        isLoading={busy === "refresh"}
        data-testid="rds-refresh"
      >
        Refresh
      </Button>
    </HStack>
  );
}

/** What is pulling PHI out of Supabase into RDS right now, in a few words. */
function mirrorPullText(vm) {
  if (vm.paused) return "nothing — RDS paused";
  if (vm.independent) return vm.catchupActive ? "catch-up on" : "catch-up off";
  if (!vm.mirror.present) return "mirror (age unknown)";
  return vm.mirror.ageSeconds == null ? "mirror · never synced" : `mirror · ${vm.mirror.ageText}`;
}

/**
 * The two systems, side by side. This is the block that answers "is
 * Supabase being used? is RDS being used? both?" in one glance: each card is
 * GREEN when that system serves data right now and RED when it does not,
 * and each carries its realtime usage -- what the screens reported serving
 * from it in the last minutes, and (for RDS) what RDS's own audit log says.
 */
export function TwoSystemsPanel({ vm, compact = false }) {
  const pair = vm.systems;
  const usage = vm.usage;
  const act = vm.rdsActivity;
  const win = Math.round((usage?.windowSeconds || 300) / 60);
  const plus = usage?.floor ? "+" : "";
  const n = (v) => `${v}${plus}`;

  const shell = (sys, children) => (
    <Box
      flex="1"
      minW="260px"
      bg={`${sys.tone}.50`}
      borderWidth="2px"
      borderColor={`${sys.tone}.400`}
      borderRadius="xl"
      px={4}
      py={3}
      data-testid={`rds-system-${sys.key}`}
      data-on={sys.on ? "true" : "false"}
    >
      <HStack justify="space-between" align="center" spacing={2}>
        <HStack spacing={2}>
          <Dot tone={sys.tone} size="12px" boxShadow />
          <Text fontSize="sm" fontWeight="bold" color={`${sys.tone}.800`} letterSpacing="0.1em">
            {sys.name}{sys.key === SOURCE.RDS ? " · AWS" : ""}
          </Text>
        </HStack>
        <Badge colorScheme={sys.tone} variant="solid" fontSize="10px" px={2} py="2px" borderRadius="md">
          {sys.on ? "✓ " : "✕ "}{sys.state}
        </Badge>
      </HStack>
      <Text fontSize="xs" color="gray.700" mt={2} noOfLines={compact ? 2 : 3}>
        {sys.why}
      </Text>
      {!compact && children}
    </Box>
  );

  const u = usage || {};
  const us = u.supabase || {};
  const ur = u.rds || {};

  const supabaseUsage = (
    <VStack align="stretch" spacing={1.5} mt={3} data-testid="rds-usage-supabase">
      <SectionLabel mb={0}>Realtime usage</SectionLabel>
      <UsageRow
        label={`PHI reads that landed here · last ${win} min`}
        value={n(us.reads300 ?? 0)}
        hint="reported by screens with a switch (fallbacks, shadow compares); sign-in and non-PHI reads are not counted"
        tone={us.reads300 ? ON_TONE : "gray"}
        testid="rds-usage-supabase-reads"
      />
      <UsageRow
        label={`PHI writes kept here · last ${win} min`}
        value={n(us.writes300 ?? 0)}
        tone={us.writes300 ? ON_TONE : "gray"}
      />
      <UsageRow label="Right now · last 60 s" value={n(us.recent ?? 0)} tone={us.recent ? ON_TONE : "gray"} />
      <UsageRow label="Last reported" value={us.lastText || "nothing reported yet"} />
      <UsageRow
        label="Pulled into RDS by"
        value={mirrorPullText(vm)}
        hint="the mirror / catch-up reads Supabase to copy PHI into RDS"
      />
    </VStack>
  );

  const rdsUsage = (
    <VStack align="stretch" spacing={1.5} mt={3} data-testid="rds-usage-rds">
      <SectionLabel
        mb={0}
        right={act?.available ? (
          <StatePill tone={act.busy ? ON_TONE : "gray"}>{act.perMinute} PHI/min</StatePill>
        ) : null}
      >
        Realtime usage
      </SectionLabel>
      <UsageRow
        label={`Reads served from RDS · last ${win} min`}
        value={n(ur.reads300 ?? 0)}
        hint="as reported by the screens that read them"
        tone={ur.reads300 ? ON_TONE : "gray"}
        testid="rds-usage-rds-reads"
      />
      <UsageRow
        label={`Writes forwarded to RDS · last ${win} min`}
        value={n(ur.writes300 ?? 0)}
        tone={ur.writes300 ? ON_TONE : "gray"}
      />
      <UsageRow label="Right now · last 60 s" value={n(ur.recent ?? 0)} tone={ur.recent ? ON_TONE : "gray"} />
      <UsageRow label="Last reported" value={ur.lastText || "nothing reported yet"} />
      {act?.available ? (
        <>
          <UsageRow
            label={`RDS audit log · last ${Math.round(act.windowSeconds / 60)} min`}
            value={`${act.phiEvents} PHI event${act.phiEvents === 1 ? "" : "s"}`}
            hint={`${act.reads} reads · ${act.writes} writes · ${act.misses} misses · ${act.admin} admin ops · ${act.viewers} viewer${act.viewers === 1 ? "" : "s"} · ${act.activeTables} table${act.activeTables === 1 ? "" : "s"}`}
            tone={act.busy ? ON_TONE : "gray"}
            testid="rds-usage-rds-audit"
          />
          <UsageRow label="Last RDS event" value={act.newestText} />
          <ActivityBars slots={act.timeline} field="phi" unit="PHI events" testid="rds-activity-bars" />
        </>
      ) : (
        <UsageRow
          label="RDS audit log"
          value="unavailable"
          hint={vm.sources.rds.reachable ? "RDS's /admin/activity did not answer" : "RDS is not answering"}
          testid="rds-usage-rds-audit"
        />
      )}
      <UsageRow
        label="Database"
        value={vm.readiness?.database?.connected ? `${vm.readiness.database.latency_ms} ms` : "down"}
        tone={vm.readiness?.database?.connected ? ON_TONE : OFF_TONE}
      />
    </VStack>
  );

  return (
    <>
      <SectionLabel
        right={pair ? <SystemPair systems={pair} size="xs" testidPrefix="rds-systems-pair" /> : null}
      >
        Who is serving the data — {pair?.headline || "…"}
      </SectionLabel>
      <HStack spacing={3} align="stretch" flexWrap="wrap">
        {pair?.supabase && shell(pair.supabase, supabaseUsage)}
        {pair?.rds && shell(pair.rds, rdsUsage)}
      </HStack>
      {!compact && (
        <Text fontSize="10px" color="gray.400" mt={2}>
          Counts come from the backend&apos;s in-memory report log (last 20 outcomes per screen, this Django process)
          and from RDS&apos;s own audit log. Sign-in and non-PHI reads never go through a switch, so Supabase&apos;s count
          is only the PHI traffic that landed on it{u.floor ? "; a + means the log was full, so the real count is higher" : ""}.
        </Text>
      )}
    </>
  );
}

/**
 * The precondition strip. Seven checks in a row, each one a yes/no the
 * operator can read without opening anything.
 */
export function PreconditionStrip({ vm }) {
  return (
    <Box>
      <SectionLabel>Preconditions</SectionLabel>
      <Box
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        p={4}
        boxShadow="0 1px 2px rgba(16,24,40,0.04)"
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
          gap={3}
        >
          {vm.checks.map((c) => (
            <HStack key={c.key} spacing={2} align="flex-start">
              <Box
                w="18px"
                h="18px"
                borderRadius="full"
                bg={`${c.tone}.100`}
                color={`${c.tone}.600`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                mt="1px"
              >
                <Text fontSize="10px" fontWeight="bold">{c.ok === true ? "✓" : c.ok === false ? "!" : "–"}</Text>
              </Box>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="bold" color="gray.700" noOfLines={1}>
                  {c.label}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={2}>
                  {c.detail}
                </Text>
              </Box>
            </HStack>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** A one-line note with an info icon, for the small print under a block. */
export function Note({ children, tone = "gray", icon = true }) {
  return (
    <HStack spacing={2} align="flex-start">
      {icon && <InfoOutlineIcon color={`${tone}.400`} boxSize="12px" mt="2px" />}
      <Text fontSize="xs" color={`${tone}.600`}>{children}</Text>
    </HStack>
  );
}
